/**
 * Bridge to the Electron desktop shell.
 *
 * The reception SPA is the same bundle on web, Capacitor native, and the
 * desktop shell (apps/desktop). On desktop the shell injects a context-isolated
 * `window.superhost` bridge (apps/desktop/src/preload/index.ts). Only the
 * surface the renderer actually calls is typed here; the shell's bridge is
 * broader.
 *
 * Why the renderer can't sign in socially the normal way on desktop: the shell
 * blocks any navigation off its loopback origin, and Google refuses OAuth in
 * embedded webviews, so `authClient.signIn.social()` (which signs in by
 * navigating the page to the provider) can never complete. The shell runs the
 * flow in the system browser instead and hands the session back over the
 * loopback interface (apps/desktop/src/main/oauth.ts).
 */

import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { linkedSupportChatPath } from "@/lib/chats-path";

type DesktopSocialProvider = "apple" | "google";

type BridgeResult = { ok: true } | { ok: false; error: string };

export interface DesktopBridge {
  /** Absent from a shell that predates support links. */
  onSupportLink?: (listener: (code: string) => void) => () => void;
  signInWithProvider: (
    provider: DesktopSocialProvider
  ) => Promise<BridgeResult>;
}

/** The desktop shell's bridge, or null outside the shell. */
export function desktopBridge(): DesktopBridge | null {
  if (typeof window === "undefined") {
    return null;
  }
  const bridge = (window as { superhost?: Partial<DesktopBridge> }).superhost;
  return bridge && typeof bridge.signInWithProvider === "function"
    ? (bridge as DesktopBridge)
    : null;
}

/** Whether the app is running inside the Electron desktop shell. */
export function isDesktopShell(): boolean {
  return desktopBridge() !== null;
}

/**
 * Run a social sign-in through the desktop shell and, on success, navigate into
 * the authenticated app — the same landing the web redirect flow reaches, so
 * the reactive session picks up the cookie the shell just placed in Electron's
 * jar. On failure the message is returned so the button can surface it and
 * clear its loading state. On success navigation takes over (loading stays on).
 */
export async function completeDesktopSocialSignIn(
  bridge: DesktopBridge,
  provider: DesktopSocialProvider,
  landingUrl: string
): Promise<BridgeResult> {
  const result = await bridge.signInWithProvider(provider);
  if (result.ok) {
    window.location.assign(landingUrl);
  }
  return result;
}

/**
 * Open the Vidimo team chat on the draft a desktop support link names, in
 * place, keeping whatever else is on screen. The code comes over a
 * public scheme, so only one this app knows opens anything.
 */
export function useDesktopSupportLink(): void {
  const history = useHistory();
  useEffect(
    () =>
      desktopBridge()?.onSupportLink?.((code) => {
        const path = linkedSupportChatPath(code);
        if (path) {
          history.push(path);
        }
      }),
    [history]
  );
}
